import { createApi } from '@reduxjs/toolkit/query/react';
import { envelopeDataTransformResponse } from 'core';

import { axiosBaseQuery } from '../axiosBaseQuery';

export type InboxDocument = {
  name: string;
  id: number;
  notificationSentOn: string | undefined;
  description: string;
  isDownloaded: boolean;
  sessionName: string;
  awardingBodyName: string;
  candidateFullName: string;
  candidateId: number;
  messageType: 'Document' | 'StatementOfEntry';
};

export type DetailsResponseResult = {
  Data: DataType[];
  IsFailure: boolean;
  IsSuccess: boolean;
};

export type NumberOfMessagesInInboxResponseResult = {
  Value: Number;
  IsFailure: boolean;
  IsSuccess: boolean;
};

export type NumberOfMessagesInInbox = {
  NumberOfMessagesInInbox: Number;
};
export type DataType = {
  Name: string;
  Documents: DocumentModel[];
};

export type Documents = {
  Documents: DataType[];
};

export type DocumentModel = {
  Id: number;
  Name: string;
  Description: string;
  Date: string;
  Candidate: string;
  NewDocument: boolean;
};

export const inboxApi = createApi({
  reducerPath: 'inboxApi',
  tagTypes: ['InboxMessages', 'UnreadMessagesCount'],
  baseQuery: axiosBaseQuery({
    baseUrl: '/web-api/b2c/api',
  }),
  endpoints: (builder) => ({
    getInboxDocuments: builder.query<InboxDocument[], void>({
      query: () => ({
        url: '/inbox/messages',
        method: 'GET',
      }),
      transformResponse: envelopeDataTransformResponse,
      providesTags: ['InboxMessages'],
    }),
    getUnreadMessagesCount: builder.query<number, void>({
      query: () => ({
        url: '/inbox/unreadMessages',
        method: 'GET',
      }),
      transformResponse: envelopeDataTransformResponse,
      providesTags: ['UnreadMessagesCount'],
    }),
    downloadDocument: builder.mutation<
      { file: Blob; headers: { [key: string]: string } },
      { documentId: number; candidateId: number }
    >({
      query: ({ documentId, candidateId }) => ({
        url: `/inbox/messages/document/${documentId}/downloaded`,
        method: 'PUT',
        config: { responseType: 'blob' },
        data: { candidateId },
      }),
      invalidatesTags: ['InboxMessages', 'UnreadMessagesCount'],
      transformResponse: (file: Blob, meta: { headers: { [key: string]: string } }) => ({
        file,
        headers: meta.headers,
      }),
    }),
    downloadStatemantOfEntry: builder.mutation<
      { file: Blob; headers: { [key: string]: string } },
      { statementOfEntryId: number; candidateId: number }
    >({
      query: ({ statementOfEntryId, candidateId }) => ({
        url: `/inbox/messages/statementOfEntry/${statementOfEntryId}/downloaded`,
        method: 'PUT',
        config: { responseType: 'blob' },
        data: { candidateId },
      }),
      invalidatesTags: ['InboxMessages', 'UnreadMessagesCount'],
      transformResponse: (file: Blob, meta: { headers: { [key: string]: string } }) => ({
        file,
        headers: meta.headers,
      }),
    }),
  }),
});

export const {
  useGetInboxDocumentsQuery,
  useDownloadDocumentMutation,
  useDownloadStatemantOfEntryMutation,
  useLazyGetUnreadMessagesCountQuery,
  endpoints,
} = inboxApi;
